import type { ReactNode } from 'react';
import { ApiError } from '../api/client';
import { useT } from '../i18n/I18nProvider';

/** Accessible loading indicator. */
export function Loading({ label }: { label?: string }): JSX.Element {
  const t = useT();
  return (
    <div className="state state--loading" role="status" aria-live="polite" aria-busy="true">
      <span className="spinner" aria-hidden="true" />
      <span>{label ?? t('states.loading')}</span>
    </div>
  );
}

/** Empty-collection placeholder with an optional call to action. */
export function EmptyState({
  title,
  hint,
  action,
}: {
  title?: string;
  hint?: string;
  action?: ReactNode;
}): JSX.Element {
  const t = useT();
  return (
    <div className="state state--empty" role="status">
      <p className="state__title">{title ?? t('states.empty')}</p>
      <p className="state__hint">{hint ?? t('states.emptyHint')}</p>
      {action}
    </div>
  );
}

/** Error panel with a retry affordance. */
export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="state state--error" role="alert">
      <p className="state__title">{t('states.error')}</p>
      <p className="state__hint">{message}</p>
      {onRetry ? (
        <button type="button" className="btn" onClick={onRetry}>
          {t('common.retry')}
        </button>
      ) : null}
    </div>
  );
}

/** A distinct state for a 403: the request worked, the caller just isn't allowed. */
export function PermissionDenied(): JSX.Element {
  const t = useT();
  return (
    <div className="state state--error" role="alert">
      <p className="state__title">{t('states.permissionDenied')}</p>
      <p className="state__hint">{t('states.permissionDeniedHint')}</p>
    </div>
  );
}

/**
 * Renders the right state for a failed async load. A 403 is not an error to retry — it is
 * a permission boundary — so it gets its own message; everything else is a retryable error.
 * Using this instead of a bare {@link ErrorState} means "you don't have access" never reads
 * as "something broke".
 */
export function AsyncError({
  error,
  onRetry,
}: {
  error: Error;
  onRetry?: () => void;
}): JSX.Element {
  if (error instanceof ApiError && error.status === 403) return <PermissionDenied />;
  return <ErrorState message={error.message} {...(onRetry ? { onRetry } : {})} />;
}

export type BannerVariant = 'success' | 'warning' | 'error' | 'info';

/** Inline status banner. */
export function Banner({
  variant,
  children,
}: {
  variant: BannerVariant;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className={`banner banner--${variant}`}
      role={variant === 'error' || variant === 'warning' ? 'alert' : 'status'}
    >
      {children}
    </div>
  );
}
